/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { page as SamplePage } from '../../help/sample_pages/ChartPage';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './ChartJSTooltip';

const ChartJSTooltipManager: ComponentManagerFn<'ChartJSTooltip'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ChartJSTooltip'>(platform, 'ChartJSTooltip'),
    type: 'ChartJSTooltip',
    name: 'Chart JS Tooltip',
    isChildAllowed: (page, item, childType) =>
      (['Box'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      open: {
        label: 'Open',
        type: 'boolean',
      },
      toolTipOnTop: {
        label: 'Tooltip On Top',
        type: 'boolean',
      },
      logDataToConsole: {
        label: 'Log Data To Console',
        type: 'boolean',
      },
    },
    category: 'Dashboard',
    canHaveChildren: () => true,
    icon: { iconName: 'comment-alt-lines' },
    description: `**Overview**<br/>
The Chart JS Tooltip component enables you to bypass the standard, plain-text tooltips of a chart and render a fully custom UI instead. By placing this component inside a Chart.js chart, you can design rich, interactive tooltips using standard layout components.
This is essential for dashboards where simple numeric labels aren't enough. You can use it to display multiple KPIs, formatted currency, icons, or conditional logic whenever a user hovers over a specific data point.

**1. Integration & Layout**<br/>
This component acts as a bridge between the Chart.js rendering engine and your visual components.
* **Parent Requirement:** This component must be placed directly inside a Chart.js component to function. It will automatically intercept hover events on that chart.
* **Designing the UI:** The tooltip uses a Box component as its canvas. You must add a Box as a child of the Chart JS Tooltip to define the layout (e.g., background color, padding, text). Inside that Box, you can use expressions to bind to the specific data point currently being hovered (e.g., tooltip.value or tooltip.label).

**2. Positioning Behavior**<br/>
Control where the tooltip appears relative to the data point.
* **Tooltip On Top:**
    * **Enabled (True):** Forces the tooltip to render directly above the hovered bar or point. This is useful for bar charts to prevent the tooltip from obscuring the bar itself.
    * **Disabled (False):** Uses the default positioning logic (usually following the mouse cursor or centering on the point).

**3. Visibility & Debugging**<br/>
These settings help you manage the tooltip state during development and production.
* **Open:** A boolean flag that can programmatically control visibility, though typically the hover action manages this automatically.
* **Log Data To Console:** A developer utility. When enabled, hovering over a chart segment will logs the raw data context (values, labels, dataset info) to the browser's developer console. This is invaluable when writing expressions to ensure you are targeting the correct data fields.`,
    helpRenders: [
      {
        title: 'Custom Tooltip',
        description:
          'Toggle the Tooltip on Top prop to see the change in the tooltip position.',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 280,
        visibleProps: ['toolTipOnTop'],
        item: {
          children: ['box'],
          itemId: 'chartJsTooltip',
          parent: 'chartjs',
          props: {
            toolTipOnTop: true,
          },
          type: 'ChartJSTooltip',
        },
      },
    ],
  };
};

export default ChartJSTooltipManager;
